/**
 * Format Unix timestamp (seconds) to localized date string (short format)
 * Format: "10 Jan Sat, 12:00" (or localized equivalent)
 */
export const formatEventDate = (timestamp: number, locale: string = 'en'): string => {
  const date = new Date(timestamp * 1000);
  
  // Map language codes to locale strings
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'tr': 'tr-TR',
    'es': 'es-ES',
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR'
  };
  
  const dateLocale = localeMap[locale] || 'en-US';
  
  const day = date.getDate();
  const month = new Intl.DateTimeFormat(dateLocale, { month: 'short' }).format(date);
  const dayName = new Intl.DateTimeFormat(dateLocale, { weekday: 'short' }).format(date);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${day} ${month} ${dayName}, ${hours}:${minutes}`;
};

/**
 * Format date range for event (short format)
 * Format: "10 Jan Sat, 12:00-18:00" (same day) or "10 Jan Sat, 12:00 - 11 Jan Sun, 18:00" (different days)
 * (or localized equivalent)
 */
export const formatEventDateRange = (startTimestamp: number, endTimestamp: number, locale: string = 'en'): string => {
  const startDate = new Date(startTimestamp * 1000);
  const endDate = new Date(endTimestamp * 1000);
  
  // Map language codes to locale strings
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'tr': 'tr-TR',
    'es': 'es-ES',
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR'
  };
  
  const dateLocale = localeMap[locale] || 'en-US';
  
  // Check if start and end are on the same day
  const isSameDay = startDate.toDateString() === endDate.toDateString();
  
  const startDay = startDate.getDate();
  const startMonth = new Intl.DateTimeFormat(dateLocale, { month: 'short' }).format(startDate);
  const startDayName = new Intl.DateTimeFormat(dateLocale, { weekday: 'short' }).format(startDate);
  const startHours = startDate.getHours().toString().padStart(2, '0');
  const startMinutes = startDate.getMinutes().toString().padStart(2, '0');
  const endHours = endDate.getHours().toString().padStart(2, '0');
  const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
  
  if (isSameDay) {
    // Same day: show date once with time range
    return `${startDay} ${startMonth} ${startDayName}, ${startHours}:${startMinutes}-${endHours}:${endMinutes}`;
  } else {
    // Different days: show full date range
    const endDay = endDate.getDate();
    const endMonth = new Intl.DateTimeFormat(dateLocale, { month: 'short' }).format(endDate);
    const endDayName = new Intl.DateTimeFormat(dateLocale, { weekday: 'short' }).format(endDate);
    return `${startDay} ${startMonth} ${startDayName}, ${startHours}:${startMinutes} - ${endDay} ${endMonth} ${endDayName}, ${endHours}:${endMinutes}`;
  }
};

/**
 * Format date only (without time) - short format
 * Format: "10 Jan 2025" (or localized equivalent)
 * Shows year only if different from current year
 */
export const formatDateOnly = (timestamp: number, locale: string = 'en'): string => {
  const date = new Date(timestamp * 1000);
  const currentYear = new Date().getFullYear();
  
  // Map language codes to locale strings
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'tr': 'tr-TR',
    'es': 'es-ES',
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR'
  };
  
  const dateLocale = localeMap[locale] || 'en-US';
  
  const day = date.getDate();
  const month = new Intl.DateTimeFormat(dateLocale, { month: 'short' }).format(date);
  const year = date.getFullYear();
  
  // Only include year if it's different from current year
  if (year !== currentYear) {
    return `${day} ${month} ${year}`;
  }
  
  return `${day} ${month}`;
};

/**
 * Format time only
 * Format: "12:00"
 */
export const formatTimeOnly = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

/**
 * Convert date string to Unix timestamp
 * Input format: "YYYY-MM-DD HH:mm"
 */
export const dateStringToTimestamp = (dateString: string): number => {
  return Math.floor(new Date(dateString).getTime() / 1000);
};

/**
 * Format date object to localized date string with short month
 * Format: "26 Apr" (or localized equivalent)
 */
export const formatDateShort = (date: Date, locale: string = 'tr'): string => {
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'tr': 'tr-TR',
    'es': 'es-ES',
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR'
  };

  const dateLocale = localeMap[locale] || 'tr-TR';
  const day = date.getDate();
  const month = new Intl.DateTimeFormat(dateLocale, { month: 'short' }).format(date);

  return `${day} ${month}`;
};

/**
 * Format date object to localized date string
 * Format: "26 Aralık - 18 Ocak" (or localized equivalent)
 */
export const formatDate = (date: Date, locale: string = 'tr'): string => {
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'tr': 'tr-TR',
    'es': 'es-ES',
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR'
  };
  
  const dateLocale = localeMap[locale] || 'tr-TR';
  const day = date.getDate();
  const month = new Intl.DateTimeFormat(dateLocale, { month: 'long' }).format(date);
  
  return `${day} ${month}`;
};

/**
 * Format time from date object
 * Format: "20:00"
 */
export const formatTime = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Check if two dates are on the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

/**
 * Get the number of days in a month
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get the first day of the month (0 = Sunday, 6 = Saturday)
 */
export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

/**
 * Check if a date is within a range (inclusive)
 */
export const isDateInRange = (date: Date, start: Date, end: Date): boolean => {
  const dateTime = date.getTime();
  const startTime = start.getTime();
  const endTime = end.getTime();
  
  // Ensure start is before end
  const actualStart = startTime < endTime ? startTime : endTime;
  const actualEnd = startTime < endTime ? endTime : startTime;
  
  return dateTime >= actualStart && dateTime <= actualEnd;
};

/**
 * Format date for display in input field (localized)
 * Format: "15 Jan 2025" or localized equivalent
 */
export const formatDateForDisplay = (date: Date | null, locale: string = 'en'): string => {
  if (!date) return '';
  
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'tr': 'tr-TR',
    'es': 'es-ES',
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR'
  };
  
  const dateLocale = localeMap[locale] || 'en-US';
  
  return new Intl.DateTimeFormat(dateLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

/**
 * Parse date input string (YYYY-MM-DD) to Date object
 */
export const parseDateInput = (value: string): Date | null => {
  if (!value) return null;
  
  const date = new Date(value + 'T00:00:00');
  if (isNaN(date.getTime())) return null;
  
  return date;
};

/**
 * Validate date against min/max constraints
 */
export const isDateValid = (date: Date, minDate?: Date, maxDate?: Date): boolean => {
  if (minDate && date < minDate) return false;
  if (maxDate && date > maxDate) return false;
  return true;
};

